import Facade from '@/Facade'
import {
    BigWinCounterData,
    FreeSpinsActivateData,
    ShowBigWinData,
    ShowRegularWinningsData,
    WinCombination,
} from '@/modules'
import GameSignals from '@/signals/GameSignals'
import gsap from 'gsap'
import { Model } from '@/model/Model'
import { toMajorUnits } from '@/utils/CurrencyUnits'
import { capWinIncrement } from '@/states/round/MaxWinCap'
import  ModulesConfig, { BIG_WIN_LAYOUT, IBigWinLayout } from '@/modulesConfig'
import { Orientation } from '@modules/src/enums/Orientation'
import { ModulesFacade } from '@modules/src/ModulesFacade'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { ModulesManager } from '@modules/src/managers/ModulesManager'
import * as PIXI from 'pixi.js'

const BIG_WIN_COIN_TEXTURE_NAMES = [
    'M1.png',
    'M2.png',
    'M3.png',
    'M4.png',
    'M5.png',
    'M6-1.png',
    'M6.png',
    'M7.png',
]

type BigWinCoinParticle = {
    sprite: PIXI.Sprite
    velocityX: number
    velocityY: number
    rotationSpeed: number
    age: number
    lifetime: number
}

export class WinningManager {
    private static readonly WIN_LAYER_MULTIPLIER: number = 2
    private static readonly WIN_LAYER_FADE_OUT: number = 30_000
    private static readonly COMMENDATION_OVERLAY_OPACITY: number = 0.62
    private static readonly BIG_WIN_COIN_GRAVITY: number = 1080
    private static readonly BIG_WIN_COIN_AVERAGE_LIFETIME: number = 2.9
    private static readonly BIG_WIN_COIN_INITIAL_RATIO: number = 0.1
    private _isWinLayerActive: boolean
    private _winLayerTimerId: number
    private _bigWinCounters: BigWinCounterData[]
    private _tlNumbers: gsap.core.Timeline
    private _bigWinValue: number
    private _model: Model
    private readonly _tweenObj: { value: number }
    private _bigWinTargets: number[] = []
    private _bigWinCurrentLevel = 0
    private _bigWinMaxLevel = 0
    private _bigWinCountedUpTo = 0
    private _bigWinPhase: 'counting' | 'levelHold' | 'done' = 'done'
    private _bigWinRescheduleTimeoutId = -1
    private _bigWinHighestOnly = false
    private _bigWinUpdatesWinCounter = true
    private _bigWinCoinContainer: PIXI.Container | null = null
    private _bigWinCoinTextures: PIXI.Texture[] = []
    private _bigWinCoinParticles: BigWinCoinParticle[] = []
    private _bigWinCoinSpawnAccumulator = 0
    private _isBigWinCoinEmissionActive = false
    private _bigWinCoinEffectRequest = 0
    private _isWinningsTextClampPatched = false

    public constructor(model: Model) {
        GameSignals.showRegularWin.connect(this.onShowRegularWin)
        GameSignals.bigWinPresentation.connect(this.onBigWinPresentation)
        GameSignals.skipBigWin.connect(this.onSkipBigWin)
        GameSignals.showBigWinComplete.connect(this.onShowBigWinComplete)
        GameSignals.activateScatters.connect(this.onActivateScatters)
        GameSignals.startBigWinLevel.connect(this.onStartBigWinLevel)
        ;(GameSignals as any).bigWinClosing?.connect(this.stopBigWinCoinEmission)
        ModuleSignals.gameResized.connect(this.onGameResized)

        this._model = model
        this._bigWinCounters = []
        this._tweenObj = { value: 0 }
    }

    private onActivateScatters = (data: FreeSpinsActivateData) => {
        if (!data.winCurrency) {
            return
        }

        // Do not show trigger scatter winlines when free spins are activated.
        // Scatter visuals are handled by activateScatters flow in reels/UI.
    }

    private onShowRegularWin = (data: ShowRegularWinningsData) => {
        GameSignals.showWinLabels.emit(data.combinations)

        const winAmount: number = capWinIncrement(this._model, this._model.winCounter.totalWin, data.win)

        if (
            !this._model.isPromoSpinsRound() ||
            this._model.roundState?.isFreeSpins ||
            this._model.spinState?.isHoldAndWinActive
        ) {
            GameSignals.updateWinPanelValue.emit(toMajorUnits(winAmount))
        }

        if (data.win / data.bet >= WinningManager.WIN_LAYER_MULTIPLIER) {
            //Activates additional win music
            this.onActivateWinLayer()
        }
    }

    private resolveOrientation(): string {
        return String(ModulesFacade.orientation || Facade.orientation || '').toUpperCase()
    }

    private isPortrait(): boolean {
        return this.resolveOrientation() === Orientation.PORTRAIT
    }

    private resolveBigWinLayout(): IBigWinLayout {
        return this.isPortrait() ? BIG_WIN_LAYOUT.portrait : BIG_WIN_LAYOUT.landscape
    }

    private applyBigWinOrientationLayout(): void {
        const bigWin = ModulesConfig.bigWin
        if (!bigWin) {
            return
        }

        const layout = this.resolveBigWinLayout()

        bigWin.scale = layout.scale
        bigWin.position = layout.position

        if (bigWin.customFont) {
            bigWin.customFont.textFieldOffset = layout.textFieldOffset
            bigWin.customFont.fontSize = layout.fontSize
        }
    }

    private applyCommendationOverlayOpacity(): void {
        const container = (ModulesManager.getModule('bigWin') as any)?.bigWinContainer
        if (container?.darkerImage) {
            container.darkerImage.alpha = WinningManager.COMMENDATION_OVERLAY_OPACITY
        }
    }

    private onGameResized = () => {
        this.applyBigWinOrientationLayout()
        this.applyCommendationOverlayOpacity()

        const layout = this.resolveBigWinLayout()

        try {
            const container = (ModulesManager.getModule('bigWin') as any)?.bigWinContainer
            if (!container) {
                return
            }

            container.bigWinAnimation?.scale?.set(layout.scale, layout.scale)
            container.scallableContainer?.position?.set(layout.position.x, layout.position.y)

            if (container.winningsText) {
                container.winningsText.y = layout.textFieldOffset
                container.winningsText.style.fontSize = layout.fontSize
            }
        } catch {
            // module not built yet (early resize) — the next onShowBigWin applies it anyway
        }
    }

    //## Bigwin text scaling: the framework clamps the winningsText width to the viewport, but we want to clamp it to the world width instead. This is for portrait.
    private static readonly BIG_WIN_TEXT_WORLD_MARGIN = 50

    private patchWinningsTextClamp(): void {
        if (this._isWinningsTextClampPatched) {
            return
        }

        const container = (ModulesManager.getModule('bigWin') as any)?.bigWinContainer
        const originalRefresh = container?.refreshWinningsText

        if (!container || typeof originalRefresh !== 'function') {
            // Module not built yet — the next presentation retries.
            return
        }

        container.refreshWinningsText = () => {
            originalRefresh()

            // Portrait only.
            if (!this.isPortrait()) {
                return
            }

            const text = container.winningsText
            if (!text) {
                return
            }

            text.scale.set(1)

            const worldWidth = Number(ModulesFacade.currentSize?.WORLD_WIDTH)
            if (!Number.isFinite(worldWidth) || worldWidth <= 0) {
                return
            }

            // winningsText.width is in its parent's space, which scallableContainer then scales.
            const parentScaleX = Math.abs(Number(container.scallableContainer?.scale?.x)) || 1
            const maxWidth = (worldWidth - WinningManager.BIG_WIN_TEXT_WORLD_MARGIN) / parentScaleX
            const textWidth = text.width

            if (maxWidth > 0 && textWidth > maxWidth) {
                text.scale.set(maxWidth / textWidth)
            }
        }

        this._isWinningsTextClampPatched = true
    }

    
    //## Commendations States are Updated.
    private applyCommendationAnimationNames(): void {
        const container = (ModulesManager.getModule('bigWin') as any)?.bigWinContainer
        if (!container?.bigWinStagesByType) {
            return
        }

        container.bigWinStagesByType = {
            bigWin: { start: '1_bw_in', idle: '1_bw_loop' },
            megaWin: { start: '2_mw_in', idle: '2_mw_loop' },
            superMegaWin: { start: '3_uw_in', idle: '3_uw_loop' },
            ultraWin: { start: '4_umw_in', idle: '4_umw_loop' },
        }
    }

    private onBigWinPresentation = (data: ShowBigWinData) => {
        this.applyBigWinOrientationLayout()
        this.applyCommendationOverlayOpacity()
        this.applyCommendationAnimationNames()
        this.patchWinningsTextClamp()
        this.clearBigWinRescheduleTimeout()
        this.clearBigWinCounters()

        this._bigWinValue = data.win
        const bigWinObj = Facade.winningsConfig.BIG_WIN
        this._bigWinHighestOnly = Boolean(data.highestOnly)
        this._bigWinUpdatesWinCounter = !data.skipWinCounter
        this._bigWinMaxLevel = data.bigWinLevel
        this._bigWinCurrentLevel = this._bigWinHighestOnly ? data.bigWinLevel : 0
        this._bigWinCountedUpTo = 0
        this._bigWinPhase = 'counting'
        this._bigWinTargets = []
        void this.startBigWinCoinEffect()

        if (this._bigWinHighestOnly) {
            // FS: single count 0 → final win on the highest commendation only.
            this._bigWinTargets[data.bigWinLevel] = data.win
        } else {
            for (let i = 0; i <= data.bigWinLevel; i++) {
                this._bigWinTargets.push(i === data.bigWinLevel ? data.win : data.bet * bigWinObj[i].to)
            }
        }

        this._tlNumbers?.kill()
        this._tlNumbers = gsap.timeline()
        this._tweenObj.value = 0

        this._tlNumbers.eventCallback('onComplete', () => {
            this._tlNumbers?.kill()
            this._tlNumbers = null
            // End sound waits for visual final hold / skip via showBigWinComplete.
        })

        if (this._bigWinHighestOnly) {
            const tier = bigWinObj[data.bigWinLevel] || bigWinObj[0]
            this._tlNumbers.to(this._tweenObj, {
                value: data.win,
                duration: tier.time,
                ease: tier.easing,
                onStart: () => {
                    this._bigWinCurrentLevel = data.bigWinLevel
                    this._bigWinPhase = 'counting'
                },
                onComplete: () => {
                    this._bigWinCountedUpTo = data.win
                },
            })
        } else {
            for (let i = 0; i <= data.bigWinLevel; i++) {
                const amount = this._bigWinTargets[i]
                const time = bigWinObj[i].time
                const easing = bigWinObj[i].easing

                this._tlNumbers.to(this._tweenObj, {
                    value: amount,
                    duration: time,
                    ease: easing,
                    onStart: () => {
                        this._bigWinCurrentLevel = i
                        this._bigWinPhase = 'counting'
                    },
                    onComplete: () => {
                        this._bigWinCountedUpTo = amount
                    },
                })
            }
        }

        GameSignals.showBigWin.emit({
            win: toMajorUnits(data.win),
            bet: toMajorUnits(data.bet),
            level: data.bigWinLevel,
            highestOnly: this._bigWinHighestOnly,
        })

        if (!this._bigWinUpdatesWinCounter) {
            // Commendation-only (H&W / FS feature totals): do not drive the Win Meter here.
            return
        }

        if (this._bigWinHighestOnly) {
            const tier = bigWinObj[data.bigWinLevel] || bigWinObj[0]
            const counter: BigWinCounterData = {
                timerId: -1,
                amount: data.win,
                duration: tier.time * 1000,
            }
            counter.timerId = window.setTimeout(this.onBigWinCounter, 0, counter)
            this._bigWinCounters.push(counter)
        } else {
            this.createBigWinCounters(data.win, data.bet, data.bigWinLevel)
        }
    }

    private onStartBigWinLevel = () => {
        // Visual container advances after a skipped level hold; keep panel counters in sync.
        if (this._bigWinPhase !== 'levelHold') {
            return
        }

        this._bigWinPhase = 'counting'
        this._bigWinCurrentLevel = Math.min(this._bigWinCurrentLevel + 1, this._bigWinMaxLevel)
        this.clearBigWinRescheduleTimeout()
        this.scheduleCountersFrom(this._bigWinCurrentLevel, 0)
    }

    private onBigWinCounter = (counter: BigWinCounterData) => {
        const index = this._bigWinCounters.indexOf(counter)

        if (index !== -1) {
            this._model.winCounter.countWin(counter.amount, counter.duration / 1000)
            this._bigWinCounters.splice(index, 1)
        }
    }

    private onSkipBigWin = () => {
        if (this._bigWinPhase === 'done' || this._bigWinTargets.length === 0) {
            return
        }

        const finalTarget = this._bigWinTargets[this._bigWinMaxLevel] ?? this._bigWinValue
        if (
            this._bigWinCurrentLevel >= this._bigWinMaxLevel &&
            this._bigWinCountedUpTo >= finalTarget
        ) {
            // Final 3s display skip — amount already counted.
            this.clearBigWinCounters()
            this._tlNumbers?.kill()
            this._tlNumbers = null
            return
        }

        if (this._bigWinPhase === 'levelHold') {
            if (this._bigWinCurrentLevel >= this._bigWinMaxLevel) {
                this.flushRemainingBigWinToPanel()
                this._tlNumbers?.kill()
                this._tlNumbers = null
                this._bigWinPhase = 'done'
            } else {
                this.clearBigWinRescheduleTimeout()
                this.scheduleCountersFrom(this._bigWinCurrentLevel + 1, 0)
            }
            return
        }

        // Counting: jump to current level trigger threshold (not full final win).
        const target = this._bigWinTargets[this._bigWinCurrentLevel] ?? this._bigWinValue
        const delta = Math.max(0, target - this._bigWinCountedUpTo)

        this._tlNumbers?.kill()
        this._tlNumbers = null
        this._tweenObj.value = target
        this.clearBigWinCounters()

        if (delta > 0 && this._bigWinUpdatesWinCounter) {
            this._model.winCounter.countWin(delta, 0, true)
            this._bigWinCountedUpTo = target
        }

        this._bigWinPhase = 'levelHold'

        if (!this._bigWinHighestOnly && this._bigWinCurrentLevel < this._bigWinMaxLevel) {
            this.scheduleCountersFrom(this._bigWinCurrentLevel + 1, 3000)
        }
    }

    private onShowBigWinComplete = () => {
        this.stopBigWinCoinEmission()
        this.flushRemainingBigWinToPanel()
        if (this._bigWinPhase !== 'done') {
            GameSignals.playBigWinEndSound.emit()
        }
        this._bigWinPhase = 'done'
        this._bigWinValue = null
        GameSignals.bigWinSoundsComplete.emit()
    }

    private startBigWinCoinEffect = async (): Promise<void> => {
        const request = ++this._bigWinCoinEffectRequest
        this.stopBigWinCoinEffect(false)

        try {
            const sheet = (PIXI.Assets.get('bigWinCoins') ||
                (await PIXI.Assets.load('bigWinCoins'))) as PIXI.Spritesheet
            if (request !== this._bigWinCoinEffectRequest || this._bigWinPhase === 'done') {
                return
            }

            const particleParent = (ModulesManager.getModule('bigWin') as any)?.bigWinContainer as
                | PIXI.Container
                | undefined
            if (!particleParent) {
                return
            }

            const textures = BIG_WIN_COIN_TEXTURE_NAMES.map((name) => sheet?.textures?.[name]).filter(
                (texture): texture is PIXI.Texture => Boolean(texture)
            )
            if (!textures.length) {
                return
            }

            const particleContainer = new PIXI.Container()
            particleContainer.label = 'Big Win Coin Particles'
            particleContainer.zIndex = 0
            const darker = (particleParent as any).darker as PIXI.Container | undefined
            const darkerIndex = darker ? particleParent.getChildIndex(darker) : 0
            particleParent.addChildAt(particleContainer, Math.min(darkerIndex + 1, particleParent.children.length))

            this._bigWinCoinContainer = particleContainer
            this._bigWinCoinTextures = textures
            this._bigWinCoinSpawnAccumulator = 0
            this._isBigWinCoinEmissionActive = true
            const initialCoinAmount = Math.ceil(
                this.resolveBigWinCoinAmount() * WinningManager.BIG_WIN_COIN_INITIAL_RATIO
            )
            for (let index = 0; index < initialCoinAmount; index++) {
                this.spawnBigWinCoinParticle()
            }
            Facade.app?.ticker.add(this.updateBigWinCoinParticles as any)
        } catch {
            this.stopBigWinCoinEffect(false)
        }
    }

    private stopBigWinCoinEmission = (): void => {
        this._bigWinCoinEffectRequest += 1
        this._isBigWinCoinEmissionActive = false
        this._bigWinCoinSpawnAccumulator = 0
    }

    private stopBigWinCoinEffect = (invalidatePending = true): void => {
        if (invalidatePending) {
            this._bigWinCoinEffectRequest += 1
        }

        Facade.app?.ticker.remove(this.updateBigWinCoinParticles as any)
        this._bigWinCoinParticles.forEach(({ sprite }) => sprite.destroy())
        this._bigWinCoinParticles = []
        this._bigWinCoinTextures = []
        this._bigWinCoinSpawnAccumulator = 0
        this._isBigWinCoinEmissionActive = false
        this._bigWinCoinContainer?.destroy({ children: true })
        this._bigWinCoinContainer = null
    }

    private spawnBigWinCoinParticle = (): void => {
        const coinAmount = this.resolveBigWinCoinAmount()

        if (
            !this._isBigWinCoinEmissionActive ||
            !this._bigWinCoinContainer ||
            !this._bigWinCoinTextures.length ||
            this._bigWinCoinParticles.length >= coinAmount
        ) {
            return
        }

        const texture = this._bigWinCoinTextures[Math.floor(Math.random() * this._bigWinCoinTextures.length)]
        const sprite = new PIXI.Sprite(texture)
        const angle = ((205 + Math.random() * 130) * Math.PI) / 180
        const speed = 520 + Math.random() * 380
        const scale = 0.38 + Math.random() * 0.34

        sprite.anchor.set(0.5)
        sprite.position.set(0, 80)
        sprite.scale.set(scale)
        sprite.rotation = Math.random() * Math.PI * 2
        sprite.alpha = 0
        this._bigWinCoinContainer.addChild(sprite as any)

        this._bigWinCoinParticles.push({
            sprite,
            velocityX: Math.cos(angle) * speed,
            velocityY: Math.sin(angle) * speed,
            rotationSpeed: ((-240 + Math.random() * 480) * Math.PI) / 180,
            age: 0,
            lifetime: 2.4 + Math.random(),
        })
    }

    private resolveBigWinCoinAmount(): number {
        const tier = Facade.winningsConfig?.BIG_WIN?.[this._bigWinCurrentLevel]
        const configuredAmount = Number(tier?.coinAmount)

        return Number.isFinite(configuredAmount) && configuredAmount > 0 ? Math.floor(configuredAmount) : 60
    }

    private updateBigWinCoinParticles = (ticker: { deltaMS: number }): void => {
        if (!this._bigWinCoinContainer) {
            return
        }

        const bigWinContainer = this._bigWinCoinContainer.parent as any
        if (this._isBigWinCoinEmissionActive && bigWinContainer?.container?.visible === false) {
            this.stopBigWinCoinEmission()
        }

        const deltaSeconds = Math.min(0.05, ticker.deltaMS / 1000)
        if (this._isBigWinCoinEmissionActive) {
            const spawnInterval = WinningManager.BIG_WIN_COIN_AVERAGE_LIFETIME / this.resolveBigWinCoinAmount()
            this._bigWinCoinSpawnAccumulator += deltaSeconds
            while (this._bigWinCoinSpawnAccumulator >= spawnInterval) {
                this._bigWinCoinSpawnAccumulator -= spawnInterval
                this.spawnBigWinCoinParticle()
            }
        }

        for (let index = this._bigWinCoinParticles.length - 1; index >= 0; index--) {
            const particle = this._bigWinCoinParticles[index]
            particle.age += deltaSeconds
            if (particle.age >= particle.lifetime) {
                particle.sprite.destroy()
                this._bigWinCoinParticles.splice(index, 1)
                continue
            }

            particle.velocityY += WinningManager.BIG_WIN_COIN_GRAVITY * deltaSeconds
            particle.sprite.x += particle.velocityX * deltaSeconds
            particle.sprite.y += particle.velocityY * deltaSeconds
            particle.sprite.rotation += particle.rotationSpeed * deltaSeconds

            const progress = particle.age / particle.lifetime
            particle.sprite.alpha = progress < 0.08 ? progress / 0.08 : progress > 0.82 ? (1 - progress) / 0.18 : 1
        }

        if (!this._isBigWinCoinEmissionActive && this._bigWinCoinParticles.length === 0) {
            this.stopBigWinCoinEffect()
        }
    }

    private flushRemainingBigWinToPanel = () => {
        this.clearBigWinRescheduleTimeout()

        if (!this._bigWinUpdatesWinCounter) {
            this.clearBigWinCounters()
            this._bigWinCountedUpTo = this._bigWinValue ?? this._bigWinCountedUpTo
            return
        }

        let totalAmount = 0

        while (this._bigWinCounters.length > 0) {
            const counter = this._bigWinCounters.shift()
            totalAmount += counter.amount
            window.clearTimeout(counter.timerId)
        }

        const remaining = Math.max(0, (this._bigWinValue ?? 0) - this._bigWinCountedUpTo)
        totalAmount += remaining
        this._bigWinCountedUpTo = this._bigWinValue ?? this._bigWinCountedUpTo

        if (totalAmount > 0) {
            this._model.winCounter.countWin(totalAmount, 0, true)
        }
    }

    private clearBigWinCounters = () => {
        while (this._bigWinCounters.length > 0) {
            const counter = this._bigWinCounters.shift()
            window.clearTimeout(counter.timerId)
        }
    }

    private clearBigWinRescheduleTimeout = () => {
        if (this._bigWinRescheduleTimeoutId !== -1) {
            window.clearTimeout(this._bigWinRescheduleTimeoutId)
            this._bigWinRescheduleTimeoutId = -1
        }
    }

    private scheduleCountersFrom = (fromLevel: number, delayMs: number) => {
        this.clearBigWinRescheduleTimeout()
        this.clearBigWinCounters()

        if (fromLevel > this._bigWinMaxLevel || !this._bigWinTargets.length) {
            return
        }

        this._bigWinRescheduleTimeoutId = window.setTimeout(() => {
            this._bigWinRescheduleTimeoutId = -1
            this._bigWinPhase = 'counting'
            this._bigWinCurrentLevel = fromLevel

            const bigWinConfig = Facade.winningsConfig.BIG_WIN
            let prevDuration = 0
            let prevAmount = this._bigWinCountedUpTo

            for (let level = fromLevel; level <= this._bigWinMaxLevel; level++) {
                const config = bigWinConfig[level]
                const totalAmount = this._bigWinTargets[level]
                const duration = config.time * 1000
                const counter: BigWinCounterData = {
                    timerId: -1,
                    amount: totalAmount - prevAmount,
                    duration,
                }
                counter.timerId = window.setTimeout(this.onBigWinCounter, prevDuration, counter)
                this._bigWinCounters.push(counter)
                prevDuration += duration
                prevAmount = totalAmount
            }
        }, delayMs)
    }

    private createBigWinCounters(win: number, bet: number, bigWinLevel: number) {
        const bigWinConfig = Facade.winningsConfig.BIG_WIN

        let prevDuration = 0
        let prevAmount = 0
        let totalAmount = 0

        for (let level = 0; level <= bigWinLevel; level++) {
            const config = bigWinConfig[level]

            totalAmount = level === bigWinLevel ? win : config.to * bet

            const counterDelay = prevDuration
            const duration = config.time * 1000

            const counter: BigWinCounterData = {
                timerId: -1,
                amount: totalAmount - prevAmount,
                duration,
            }

            counter.timerId = window.setTimeout(this.onBigWinCounter, counterDelay, counter)
            this._bigWinCounters.push(counter)

            prevDuration = counterDelay + duration
            prevAmount = totalAmount
        }
    }

    private onActivateWinLayer = () => {
        if (this._model.roundState.isFreeSpins) {
            // no need to trigger win layer during free spins
            return
        }

        if (this._isWinLayerActive) {
            // resetting timer if an additional win occurred
            window.clearTimeout(this._winLayerTimerId)
            this._winLayerTimerId = window.setTimeout(this.onDeactivateWinLayer, WinningManager.WIN_LAYER_FADE_OUT)
            return
        }

        this._winLayerTimerId = window.setTimeout(this.onDeactivateWinLayer, WinningManager.WIN_LAYER_FADE_OUT)
        GameSignals.startWinLayerMusic.emit()
        this._isWinLayerActive = true
    }

    private onDeactivateWinLayer = () => {
        this._isWinLayerActive = false
        GameSignals.stopWinLayerMusic.emit()
        this._winLayerTimerId = -1
    }
}
